import type { TracePreviewDto } from '@terra/shared-contracts-trace';

export type NeoAttackStoreStatus =
  | 'starting'
  | 'running'
  | 'queued'
  | 'completed'
  | 'failed';

export type BackendTraceStatus = TracePreviewDto['status'];

export const NEO_ATTACK_CACHE_TTL_MS = 24 * 60 * 60 * 1000;
export const NEO_ATTACK_STARTUP_GRACE_MS = 2 * 60 * 1000;
export const NEO_ATTACK_RECONCILE_POLL_MS = 60_000;
export const NEO_ATTACK_RUNNING_FETCH_LIMIT = 100;

export function isActiveNeoAttackStoreStatus(
  status: NeoAttackStoreStatus,
): boolean {
  return status === 'starting' || status === 'running' || status === 'queued';
}

export function isTerminalNeoAttackStoreStatus(
  status: NeoAttackStoreStatus,
): boolean {
  return status === 'completed' || status === 'failed';
}

export function mapBackendTraceStatusToNeoAttackStatus(
  traceStatus: BackendTraceStatus,
): NeoAttackStoreStatus | null {
  if (traceStatus === 'RUNNING') {
    return 'running';
  }

  if (traceStatus === 'COMPLETED') {
    return 'completed';
  }

  if (
    traceStatus === 'FAILED' ||
    traceStatus === 'ABORTED' ||
    traceStatus === 'PAUSED' ||
    traceStatus === 'BLOCKED'
  ) {
    return 'failed';
  }

  return null;
}

export function isTerminalBackendTraceStatus(
  traceStatus: BackendTraceStatus,
): boolean {
  const mapped = mapBackendTraceStatusToNeoAttackStatus(traceStatus);

  return mapped === 'completed' || mapped === 'failed';
}

export function shouldInitializeNeoAttackFromBackendTrace({
  traceStatus,
  hasStoreEntry,
}: {
  traceStatus: BackendTraceStatus;
  hasStoreEntry: boolean;
}): boolean {
  return traceStatus === 'RUNNING' && !hasStoreEntry;
}

export function resolveNeoAttackStatusFromBackendTrace({
  traceStatus,
  currentStatus,
}: {
  traceStatus: BackendTraceStatus;
  currentStatus: NeoAttackStoreStatus;
}): NeoAttackStoreStatus | null {
  const mapped = mapBackendTraceStatusToNeoAttackStatus(traceStatus);

  if (!mapped || mapped === currentStatus || mapped === 'running') {
    return null;
  }

  return mapped;
}

export type NeoAttackTraceSync =
  | { kind: 'initialize' }
  | { kind: 'adopt-running' }
  | { kind: 'adopt-terminal'; status: 'completed' | 'failed' }
  | { kind: 'update'; status: NeoAttackStoreStatus }
  | { kind: 'none' };

export function resolveNeoAttackTraceSync({
  traceStatus,
  traceThreadId,
  traceCreatedAtMs,
  current,
}: {
  traceStatus: BackendTraceStatus;
  traceThreadId: string;
  traceCreatedAtMs: number;
  current: {
    status: NeoAttackStoreStatus;
    threadId: string;
    startedAtMs: number;
  } | null;
}): NeoAttackTraceSync {
  if (!current) {
    return traceStatus === 'RUNNING'
      ? { kind: 'initialize' }
      : { kind: 'none' };
  }

  if (current.threadId && current.threadId === traceThreadId) {
    const status = resolveNeoAttackStatusFromBackendTrace({
      traceStatus,
      currentStatus: current.status,
    });

    return status ? { kind: 'update', status } : { kind: 'none' };
  }

  const queuedWithoutThread =
    current.status === 'queued' && current.threadId.length === 0;
  const traceFollowsQueue =
    !Number.isNaN(traceCreatedAtMs) &&
    !Number.isNaN(current.startedAtMs) &&
    traceCreatedAtMs >= current.startedAtMs;

  if (!queuedWithoutThread || !traceFollowsQueue) {
    return { kind: 'none' };
  }

  if (traceStatus === 'RUNNING') {
    return { kind: 'adopt-running' };
  }

  const mapped = mapBackendTraceStatusToNeoAttackStatus(traceStatus);

  if (mapped === 'completed' || mapped === 'failed') {
    return { kind: 'adopt-terminal', status: mapped };
  }

  return { kind: 'none' };
}
